import {reactive}              from "vue";
import ContactPersonItem       from "./Entities/ContactPersonItem";
import ContactGroupItem        from './Entities/ContactGroupItem';
import ContactsData            from './Entities/ContactsData';
import ContactsHTTPMapping     from "./HTTP/ContactsHTTPMapping";
import ContactsHTTPService     from "./HTTP/ContactsHTTPService";
import NotificationsController from "@/js/Webmail/Notifications/NotificationsController";
import StateEventManager       from "@/js/Shared/Events/Managers/StateEventManager";
import BroadcastEventManager   from "@/js/Shared/Events/Managers/BroadcastEventManager";

export {ContactsControllerSingleton as default};

/**
* Messages List Controller
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ContactsController {
  /**
   * Holds all contacts data information
   *
   * @public
   *
   * @type {ContactsData}
   *
   * @since 7.0.0
   */
  contacts = new ContactsData();

  /**
   * HTTP Mappings
   *
   * @private
   *
   * @type {ContactsHTTPMapping}
   *
   * @since 7.0.0
   */
  http_mapping = new ContactsHTTPMapping();

  /**
   * Contacts HTTP Service
   *
   * @private
   *
   * @type {ContactsHTTPService}
   *
   * @since 7.0.0
   */
  http_service = new ContactsHTTPService();

  /**
   * Event Message
   *
   * @private
   *
   * @type {BroadcastEventManager}
   *
   * @since 7.0.0
   */
  broadcaster = new BroadcastEventManager();

  /**
   * Event state
   *
   * @private
   *
   * @type {StateEventManager}
   *
   * @since 7.0.0
   */
  state = new StateEventManager();

  /**
   * Fetches contacts from the server in the foreground
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<Boolean>}
   */
  async fetchContactsForeground() {
    return this.fetchContacts('fetch_foreground');
  }

  /**
   * Fetches contacts from the server in the background
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<Boolean>}
   */
  async fetchContactsBackground() {
    return this.fetchContacts('fetch_background');
  }

  /**
   * Fetches Contacts from the server but only if they have not being fetched before
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<Boolean>}
   */
  async fetchContactsOnce() {
    return (this.contacts.fetched) ? true : this.fetchContactsForeground();
  }

  /**
   * This method will make a server request every time is called to reload the contact list
   *
   * Bypasses any ongoing requests or previous fetches verifitions
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {Boolean}
   */
  async fetchContacts(type) {
    this.state.setLoading(type);
    let list     = await this.http_service.fetchList();
    let frequent = await this.http_service.fetchFavourites();


    if (list.success === false) {
      NotificationsController.addMessage('notifications.', list.messages);
      this.state.setSuccess(type, false);
      return false;
    }

    this.http_mapping.mapResults(this.contacts, list.results);
    this.http_mapping.mapResultsFrequent(this.contacts, frequent.results);
    this.state.setSuccess(type, true);
    return true;
  }

  /**
   * Deletes a single contact
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Array} contacts contacts to be deleted
   *
   * @return {Promise<Boolean>}
   */
  async deleteContacts(contacts, broadcast_message = 'contacts-deleted') {
    this.state.setLoading('delete')
    let response = await this.http_service.contactsDelete(contacts);

    if (response.success) {
      this.http_mapping.mapResults(this.contacts, response.results);
      this.broadcaster.broadcast(broadcast_message, contacts);
    }

    NotificationsController.addMessage('notifications.', response.messages);
    this.state.setSuccess('delete', response.success);
    return response.success;
  }

  /**
   * Calls the http service to add/update a person item
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactPersonItem} person
   *
   * @return {Promise<Boolean>}
   */
  async savePerson(person, message = 'contacts-added') {
    this.state.setLoading('save_person')
    let response     = await this.http_service.savePerson(person);
    let replacements = [person.name];

    if (response.success) {
      this.http_mapping.mapResults(this.contacts, response.results);
      this.broadcaster.broadcast(message, this.contacts.all.getLastAdded());
    }

    NotificationsController.addMessage('notifications.', response.messages, replacements);
    this.state.setSuccess('save_person', response.success);
    return response.success;
  }

  /**
   * Calls the http service to add/update a group item
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactGroupItem} group
   *
   * @return {Promise<Boolean>}
   */
  async saveGroup(group, message = 'contacts-added') {
    this.state.setLoading('group_save');
    let response     = await this.http_service.saveGroup(group);
    let replacements = [group.name];

    if (response.success) {
      this.http_mapping.mapResults(this.contacts, response.results);
      this.broadcaster.broadcast(message, this.contacts.all.getLastAdded());
    }

    NotificationsController.addMessage('notifications.', response.messages, replacements);
    this.state.setSuccess('group_save', response.success);
    return response.success;
  }

  /**
   * Adds a list of persons to a group
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactGroupItem} group
   * @param {Array}            persons  contacts to be deleted
   *
   * @return {Promise<Boolean>}
   */
   async addPersonsToGroup(group, persons) {
    this.state.setLoading('group_add_persons');
    let response = await this.http_service.addPersonsToGroup(group.id, persons);
    let replacements = [group.name];

    if (response.success) {
      this.http_mapping.mapResults(this.contacts, response.results);
    }

    NotificationsController.addMessage('notifications.', response.messages, replacements);
    this.state.setSuccess('group_add_persons', response.success);
    return response.success;
  }

  /**
   * Removes a list of persons from a group
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ContactGroupItem} group
   * @param {Array}            persons  contacts to be deleted
   *
   * @return {Promise<Boolean>}
   */
  async removePersonsFromGroup(group, persons) {
    this.state.setLoading('group_remove_persons');
    let response     = await this.http_service.removePersonsFromGroup(group.id, persons);
    let replacements = [group.name];

    if (response.success) {
      this.http_mapping.mapResults(this.contacts, response.results);
    }

    NotificationsController.addMessage('notifications.', response.messages, replacements);
    this.state.setSuccess('group_remove_persons', response.success);
    return response.success;
  }

  /**
   * Save all contacts from content file
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} contacts
   *
   * @return {boolean}
   */
  async importContacts(contacts) {
    this.state.setLoading('contacts-import');
    let response = await this.http_service.importContacts(contacts);

    if (response.success) {
      this.http_mapping.mapResults(this.contacts, response.results);
      this.broadcaster.broadcast('contacts-reload', this.contacts.all);
      this.broadcaster.broadcast('contacts-import');
    }

    NotificationsController.addMessage('notifications.', response.messages);
    this.state.setSuccess('contacts-import', response.success);
    return response.success;
  }
}

/**
 * Contacts Controller Singleton class
 *
 * Handles all contact data
 *
 * @public
 *
 * @type {ContactsController}
 *
 * @since 7.0.0
 */
const ContactsControllerSingleton = reactive(new ContactsController());